export function isSiteRootWebPath(url: string | null | undefined): boolean {
  const trimmed = (url ?? "").trim();
  return trimmed === "" || trimmed === "/";
}

/** Resolves internal (Web in) link paths. Empty or "/" means site root. */
export function resolveInternalWebHref(url: string | null | undefined): string {
  const trimmed = (url ?? "").trim();
  if (isSiteRootWebPath(trimmed)) {
    return "/";
  }
  if (trimmed.startsWith("/")) {
    return trimmed;
  }
  return `/${trimmed}`;
}

/** Resolves external (Web out) link URLs. Empty or "/" means site root, not current page. */
export function resolveWebOutHref(url: string | null | undefined): string {
  const trimmed = (url ?? "").trim();
  if (isSiteRootWebPath(trimmed)) {
    return "/";
  }
  if (trimmed.startsWith("/")) {
    return trimmed;
  }
  if (trimmed.includes("http")) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

export function resolvePersistedLinkUrl(
  type: string | null | undefined,
  url: string | null | undefined,
  pageUrl?: string | null
): string | undefined {
  if (type === "Web") {
    return resolveInternalWebHref(url);
  }
  if (type === "Web_out") {
    return resolveWebOutHref(url);
  }

  return url || pageUrl || undefined;
}

type BuilderPageLike = {
  id?: number | string;
  uuid?: string;
  url?: string;
  languageCode?: string;
};

type BuilderLinkLike = {
  pageId?: number | string;
  page_id?: number | string;
  pageUuid?: string | null;
  page_uuid?: string | null;
  webpageUuid?: string | null;
  webpage_uuid?: string | null;
  type?: string | null;
  url?: string | null;
};

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function resolvePageByNumericId(
  pages: BuilderPageLike[],
  value: unknown
): BuilderPageLike | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return pages.find((page) => Number(page.id) === value);
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) {
      return pages.find((page) => Number(page.id) === parsed);
    }
  }
  return undefined;
}

export function resolvePageByLink(
  pages: BuilderPageLike[] | undefined,
  link: BuilderLinkLike | null | undefined
): BuilderPageLike | undefined {
  if (!pages || !link) {
    return undefined;
  }

  const pageUuid = normalizeText(
    link.pageUuid ?? link.page_uuid ?? link.webpageUuid ?? link.webpage_uuid
  );
  if (pageUuid) {
    const page = pages.find((item) => normalizeText(item.uuid) === pageUuid);
    if (page) {
      return page;
    }
  }

  const legacyPage =
    resolvePageByNumericId(pages, link.pageId) ??
    resolvePageByNumericId(pages, link.page_id);
  if (legacyPage) {
    return legacyPage;
  }

  const pageUrl = normalizeText(link.url);
  if (link.type === "Page" && pageUrl) {
    return pages.find((item) => normalizeText(item.url) === pageUrl);
  }

  return undefined;
}

export function resolveBuilderLinkHref(
  link: BuilderLinkLike | null | undefined,
  pages: BuilderPageLike[] | undefined,
  getPageHref: (page: BuilderPageLike) => string
): string | undefined {
  const type = link?.type;
  const url = normalizeText(link?.url);

  if (type === "Page") {
    const page = resolvePageByLink(pages, link);
    return page ? getPageHref(page) : undefined;
  }
  if (type === "Web_out") {
    return resolveWebOutHref(url);
  }
  if (type === "Web") {
    return resolveInternalWebHref(url);
  }
  if (type === "None") {
    return "";
  }
  if (type === "Anchor") {
    return url.startsWith("#") ? url : `#${url}`;
  }
  if (type === "Phone") {
    return url.startsWith("tel:") ? url : `tel:${url}`;
  }
  if (type === "Email") {
    return url.startsWith("mailto:") ? url : `mailto:${url}`;
  }

  return undefined;
}

export function appendForwardedUrlParams(
  href: string | undefined,
  linkType: string | null | undefined,
  locationSearch: string,
  locationPathname: string
): string | undefined {
  if (!href || !locationSearch) {
    return href;
  }

  if (linkType === "Anchor") {
    return locationPathname + locationSearch + href;
  }
  if (linkType === "Web_out") {
    const separator = href.includes("?") ? "&" : "?";
    const rawParams = locationSearch.startsWith("?")
      ? locationSearch.slice(1)
      : locationSearch;
    return href + separator + rawParams;
  }
  if (linkType === "Page" || linkType === "Web" || linkType === "Email") {
    return href + locationSearch;
  }

  return href;
}
